/*
  ALAC cart upsell (cart drawer + PDP).
  - alac-free-shipping: announces free-shipping progress changes in one persistent live region.
  - alac-recommendations: lazy recommendations from Shopify's Product Recommendations API
    (complementary first, related as fallback) plus the store's own format collections.
  - alac-subscribe: per-line Subscribe & save toggle (native selling plans, opt-in only).
  - alac-gap-closer: one-tap offer that gets a cart under the free-shipping threshold to free shipping.
  - [data-alac-add] buttons: one-tap add (try another roast, empty cart picks).
  Every cart write goes through one serialized queue (mutate) and renders once per action.
  Native Shopify endpoints only. No third-party scripts.
*/
(function () {
  if (window.customElements.get('alac-recommendations')) return;

  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var recCache = new Map();

  /* ---------- Shared cart helpers ---------- */
  function postJSON(path, body) {
    return fetch(root + path, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then(function (r) {
      return r.json().then(function (d) {
        if (!r.ok) throw new Error((d && (d.description || d.message)) || 'Cart error');
        return d;
      });
    });
  }

  function withSections(body) {
    if (document.querySelector('cart-drawer')) {
      body.sections = 'cart-drawer,cart-icon-bubble';
      body.sections_url = window.location.pathname;
    }
    return body;
  }

  // Fresh cart + drawer sections, used when an action failed and left no cart response to render.
  function fetchCartState() {
    var get = function (url) {
      return fetch(url, { credentials: 'same-origin', headers: { Accept: 'application/json' } }).then(function (r) {
        if (!r.ok) throw new Error('Cart error');
        return r.json();
      });
    };
    return Promise.all([get(root + 'cart.js'), get(window.location.pathname + '?sections=cart-drawer,cart-icon-bubble')]).then(function (res) {
      res[0].sections = res[1];
      return res[0];
    });
  }

  function applyCart(data, source) {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer || !data.sections) {
      window.location.href = root + 'cart';
      return false;
    }
    drawer.classList.toggle('is-empty', data.item_count === 0);
    drawer.renderContents(data);
    // Other listeners (PDP quantity rules, price per item) still hear about the change. The source
    // is 'cart-items' because cart-drawer-items skips that source: the drawer was just rendered
    // from this response, and a second re-fetch would render it twice and drop focus and errors.
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', alacSource: source, cartData: data });
    }
    return true;
  }

  function setBusy(button, busy) {
    if (!button) return;
    button.disabled = busy;
    button.classList.toggle('is-loading', busy);
    if (busy) button.setAttribute('aria-busy', 'true');
    else button.removeAttribute('aria-busy');
  }

  /* ---------- Announcements: one persistent polite region for success and progress ---------- */
  var liveRegion = null;
  function say(message) {
    if (!message) return;
    if (!liveRegion || !liveRegion.isConnected) {
      liveRegion = document.createElement('p');
      liveRegion.className = 'visually-hidden';
      liveRegion.setAttribute('aria-live', 'polite');
      liveRegion.setAttribute('role', 'status');
      document.body.appendChild(liveRegion);
    }
    liveRegion.textContent = '';
    setTimeout(function () {
      liveRegion.textContent = message;
    }, 50);
  }

  // Errors go into a role="alert" element. The element is looked up after any re-render, so the
  // message lands in the live DOM (the old node may have been replaced).
  function showError(selector) {
    var drawer = document.querySelector('cart-drawer');
    var scope = drawer || document;
    var target = (selector && scope.querySelector(selector)) || (drawer && drawer.querySelector('#CartDrawer-CartErrors'));
    if (!target) return;
    var source = scope.querySelector('[role="alert"][data-message]');
    var text = target.dataset.message || (source && source.dataset.message) || 'There was an error while updating your cart. Please try again.';
    target.hidden = false;
    target.textContent = '';
    setTimeout(function () {
      target.textContent = text;
    }, 50);
  }

  // After a re-render, put focus back on the control that started the action, or on the
  // nearest sensible element if that control is gone (for example the gap closer after it
  // unlocked free shipping).
  function restoreFocus(keys, attempt) {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer || !drawer.classList.contains('active')) return;
    var candidates = (keys || []).filter(Boolean).concat(['.alac-fs__msg', '.drawer__heading']);
    for (var i = 0; i < candidates.length; i++) {
      var node = drawer.querySelector(candidates[i]);
      if (!node || node.disabled || node.closest('[hidden]') || !node.getClientRects().length) continue;
      if (node.tabIndex < 0 && !node.matches('a[href], button, input, select, textarea')) node.setAttribute('tabindex', '-1');
      node.focus({ preventScroll: false });
      // The drawer can still be settling (its open() runs on a timer); try again briefly.
      if (document.activeElement !== node && (attempt || 0) < 4) {
        setTimeout(function () {
          restoreFocus(keys, (attempt || 0) + 1);
        }, 150);
      }
      return;
    }
  }

  /* ---------- Cart mutation queue ----------
     Every alac cart write runs through one queue, so only one request is in flight at a time.
     Each action renders the drawer at most once, from its own final response. If a newer
     action was queued meanwhile, the older response is stale and is not rendered (the newer
     action renders a fresher cart). */
  var queue = Promise.resolve();
  var issued = 0;
  var pending = 0;

  function mutate(options) {
    var token = ++issued;
    pending += 1;
    var job = queue.then(function () {
      return options.task();
    });
    queue = job.then(
      function () {},
      function () {}
    );
    var finish = function () {
      pending -= 1;
    };
    return job.then(
      function (data) {
        finish();
        if (token !== issued) {
          // Stale: a newer action is queued and will render a fresher cart.
          if (options.onError) options.onError(false);
          return data;
        }
        if (applyCart(data, options.source)) {
          say(options.success);
          setTimeout(function () {
            restoreFocus(options.focus);
          }, 60);
        }
        return data;
      },
      function (error) {
        finish();
        var render = token === issued && error && error.cart ? Promise.resolve(error.cart) : token === issued && options.refreshOnError ? fetchCartState().catch(function () { return null; }) : Promise.resolve(null);
        return render.then(function (data) {
          if (data) applyCart(data, options.source);
          if (options.onError) options.onError(!!data);
          setTimeout(function () {
            showError(options.errorSelector);
            if (data) restoreFocus(options.focus);
          }, data ? 60 : 0);
          throw error;
        });
      }
    );
  }

  /* ---------- Free-shipping bar: announce changes through the polite region ---------- */
  var lastMessage = null;

  function announce(message) {
    if (!message || message === lastMessage) return;
    var first = lastMessage === null;
    lastMessage = message;
    if (first) return; // the initial render is not announced
    say(message);
  }

  class AlacFreeShipping extends HTMLElement {
    connectedCallback() {
      announce(this.dataset.message);
    }
  }
  window.customElements.define('alac-free-shipping', AlacFreeShipping);

  /* ---------- Subscribe & save per cart line ---------- */
  class AlacSubscribe extends HTMLElement {
    connectedCallback() {
      var self = this;
      this.toggle = this.querySelector('.alac-sub__toggle');
      this.select = this.querySelector('.alac-sub__select');
      this.removeButton = this.querySelector('.alac-sub__remove');
      // The theme's cart-drawer-items listens for every 'change' event under it and treats the
      // target as a quantity input (POST /cart/change with quantity "on", a 400). Subscribe
      // controls are not quantity inputs: handle them here and stop the event at this element.
      // Listeners on the controls themselves (below) still run first.
      this.addEventListener('change', function (event) {
        event.stopPropagation();
      });
      if (this.toggle) {
        this.toggle.addEventListener('change', function () {
          if (self.toggle.checked) {
            self.classList.add('is-open');
            self.apply(self.select.value, 'toggle');
          } else {
            self.apply(null, 'toggle');
          }
        });
      }
      if (this.select) {
        this.select.addEventListener('change', function () {
          self.updateDisclosure();
          if (self.dataset.subscribed === 'true' || (self.toggle && self.toggle.checked)) self.apply(self.select.value, 'plan');
        });
        this.updateDisclosure();
      }
      if (this.removeButton) {
        this.removeButton.addEventListener('click', function () {
          self.apply(null, 'remove');
        });
      }
    }

    updateDisclosure() {
      var option = this.select && this.select.selectedOptions[0];
      if (!option) return;
      var out = this.querySelector('[data-alac-sub-disclosure]');
      if (out && option.dataset.disclosure) out.textContent = option.dataset.disclosure;
      // The cadence note beside the toggle follows the chosen frequency.
      var applied = this.querySelector('[data-alac-sub-applied]');
      if (applied && option.dataset.applied) applied.textContent = option.dataset.applied;
      var fs = this.querySelector('[data-alac-sub-shipping]');
      if (fs) {
        fs.textContent = option.dataset.shipping || '';
        fs.hidden = !option.dataset.shipping;
      }
    }

    apply(planId, origin) {
      var self = this;
      var d = this.dataset;
      var uid = d.uid || 'AlacSub-' + d.line;
      var controls = this.querySelectorAll('input, select, button');
      controls.forEach(function (c) {
        c.disabled = true;
      });
      this.classList.add('is-loading');
      var body = { quantity: parseInt(d.quantity, 10), selling_plan: planId ? parseInt(planId, 10) : null };
      if (d.key) body.id = d.key;
      else body.line = parseInt(d.line, 10);
      var focus = origin === 'plan' ? ['#' + uid + '-plan', '#' + uid + '-toggle'] : ['#' + uid + '-toggle', '#' + uid + '-plan'];
      mutate({
        source: 'alac-subscribe',
        task: function () {
          return postJSON('cart/change.js', withSections(body));
        },
        focus: focus,
        errorSelector: '#' + uid + '-error',
        refreshOnError: false,
        onError: function () {
          if (!self.isConnected) return;
          self.classList.remove('is-loading');
          controls.forEach(function (c) {
            c.disabled = false;
          });
          if (self.toggle) self.toggle.checked = d.subscribed === 'true';
        },
      }).catch(function () {});
    }
  }
  window.customElements.define('alac-subscribe', AlacSubscribe);

  /* ---------- Gap closer (one-tap offer to reach free shipping) ---------- */
  class AlacGapCloser extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('button');
      if (this.button) this.button.addEventListener('click', this.run.bind(this));
      this.dataset.ready = 'true';
    }

    run() {
      var self = this;
      var d = this.dataset;
      if (this.button.disabled) return;
      var item = { id: parseInt(d.addVariant, 10), quantity: 1 };
      if (d.sellingPlan) item.selling_plan = parseInt(d.sellingPlan, 10);
      setBusy(this.button, true);
      var task;
      if (d.mode === 'upgrade') {
        // Atomic swap: the 1 Bag line goes to quantity 0 (or to the bags that stay on it, for the
        // pack switch), then one pack line is added with the same selling plan. If the add fails,
        // the original line (variant, quantity, selling plan and properties) is put back and the
        // restored cart is rendered with an error.
        var keep = parseInt(d.keepQuantity, 10) || 0;
        task = function () {
          return postJSON('cart/change.js', { id: d.removeKey, quantity: keep }).then(function () {
            return postJSON('cart/add.js', withSections({ items: [item] })).catch(function (addError) {
              if (keep > 0) {
                return postJSON('cart/change.js', withSections({ id: d.removeKey, quantity: parseInt(d.origQuantity, 10) || keep })).then(
                  function (restoredLine) {
                    addError.cart = restoredLine;
                    throw addError;
                  },
                  function () {
                    throw addError;
                  }
                );
              }
              var back = { id: parseInt(d.origVariant, 10), quantity: parseInt(d.origQuantity, 10) || 1 };
              if (d.origSellingPlan) back.selling_plan = parseInt(d.origSellingPlan, 10);
              if (d.origProperties) {
                try {
                  back.properties = JSON.parse(d.origProperties);
                } catch (e) {
                  /* keep the line without properties */
                }
              }
              return postJSON('cart/add.js', withSections({ items: [back] })).then(
                function (restored) {
                  addError.cart = restored;
                  throw addError;
                },
                function () {
                  throw addError;
                }
              );
            });
          });
        };
      } else {
        task = function () {
          return postJSON('cart/add.js', withSections({ items: [item] }));
        };
      }
      mutate({
        source: 'alac-gap-closer',
        task: task,
        success: d.success,
        focus: ['alac-gap-closer .alac-gap__button'],
        errorSelector: '[data-alac-gap-error]',
        refreshOnError: d.mode === 'upgrade',
        onError: function (rendered) {
          if (!rendered && self.isConnected) setBusy(self.button, false);
        },
      }).catch(function () {});
    }
  }
  window.customElements.define('alac-gap-closer', AlacGapCloser);

  /* ---------- One-tap add buttons (try another roast, empty cart picks) ---------- */
  document.addEventListener('click', function (event) {
    var button = event.target.closest && event.target.closest('button[data-alac-add]');
    if (!button || button.disabled) return;
    var variantId = parseInt(button.dataset.variantId, 10);
    if (!variantId) return;
    var errorSelector = button.closest('.alac-recs--empty') ? '[data-alac-empty-error]' : null;
    setBusy(button, true);
    mutate({
      source: 'alac-quick-add',
      task: function () {
        return postJSON('cart/add.js', withSections({ items: [{ id: variantId, quantity: 1 }] }));
      },
      success: button.dataset.success,
      // Same button if it is still there, else the row's new Add button (the next roast).
      focus: ['button[data-alac-add][data-variant-id="' + variantId + '"]', button.closest('.alac-try') ? '.alac-try__add' : '.alac-recs--empty .alac-recs__add'],
      errorSelector: errorSelector,
      onError: function () {
        if (button.isConnected) setBusy(button, false);
      },
    }).catch(function () {});
  });

  /* The cart drawer's delivery range is the shared <delivery-estimate> component (assets/delivery-estimate.js). */

  /* ---------- Recommendations ---------- */
  function fetchRecommendationJSON(url) {
    var controller = new AbortController();
    var timer = setTimeout(function () { controller.abort(); }, 8000);
    return fetch(url, { credentials: 'same-origin', signal: controller.signal })
      .then(function (response) {
        if (!response.ok) throw new Error('Recommendations could not load');
        return response.json();
      })
      .finally(function () { clearTimeout(timer); });
  }

  function fetchIntent(productId, intent) {
    var url = root + 'recommendations/products.json?product_id=' + encodeURIComponent(productId) + '&limit=4&intent=' + intent;
    return fetchRecommendationJSON(url)
      .then(function (d) {
        return (d && d.products) || [];
      });
  }

  function getIntent(productId, intent) {
    var key = productId + ':' + intent;
    if (!recCache.has(key)) {
      recCache.set(
        key,
        fetchIntent(productId, intent).catch(function () {
          recCache.delete(key);
          return [];
        })
      );
    }
    return recCache.get(key);
  }

  // Storefront collection JSON has decimal-string prices: normalize to the recommendations shape (cents).
  function getCollection(handle) {
    var key = 'collection:' + handle;
    if (!recCache.has(key)) {
      recCache.set(
        key,
        fetchRecommendationJSON(root + 'collections/' + encodeURIComponent(handle) + '/products.json?limit=30')
          .then(function (d) {
            return ((d && d.products) || []).map(function (p) {
              var variants = (p.variants || []).map(function (v) {
                return { id: v.id, title: v.title, available: v.available, price: Math.round(parseFloat(v.price) * 100), featured_image: v.featured_image };
              });
              var tags = Array.isArray(p.tags) ? p.tags : splitList(p.tags);
              return {
                id: p.id,
                title: p.title,
                url: root + 'products/' + p.handle,
                available: variants.some(function (v) {
                  return v.available;
                }),
                gift_card: p.product_type === 'Gift Cards',
                tags: tags,
                featured_image: p.images && p.images[0] && p.images[0].src,
                variants: variants,
              };
            });
          })
          .catch(function () {
            recCache.delete(key);
            return [];
          })
      );
    }
    return recCache.get(key);
  }

  function norm(text) {
    return String(text || '')
      .toLowerCase()
      .replace(/&/g, 'and')
      .replace(/\s+/g, ' ');
  }

  // "Alex's Low-Acid Organic Coffee(TM) - Decaf Fresh Ground (12oz)" -> "Decaf Fresh Ground (12oz)"
  function shortTitle(title) {
    var t = String(title).replace(/^alex['’]s low[- ]acid organic coffee(™)?\s*(-\s*)?/i, '');
    // Drop descriptive parentheticals like "(Tea Bag Style)", but keep sizes like "(5lbs)" or "(12oz)".
    t = t.replace(/\s*\((?![^)]*\d\s*(?:oz|lbs?)\b)[^)]*\)/gi, '').replace(/ Coffee Steep Bags/i, ' Steep Bags').trim();
    return t || title;
  }

  // Bag-count tiers ("1 Bag", "3 Bags") are multipacks, not sizes: those show the price only.
  function sizeLabel(product, variant) {
    if (!variant || (product.variants || []).length < 2) return '';
    var t = String(variant.title || '');
    if (!t || t === 'Default Title' || /^\d+\s*bags?$/i.test(t)) return '';
    var i = t.lastIndexOf(' - ');
    return i > 0 ? t.slice(i + 3) : t;
  }

  function sizedImage(src, width) {
    if (!src) return '';
    if (src.indexOf('//') === 0) src = 'https:' + src;
    return src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  function el(tag, cls, text) {
    var node = document.createElement(tag);
    if (cls) node.className = cls;
    if (text != null) node.textContent = text;
    return node;
  }

  function splitList(value, sep) {
    return String(value || '')
      .split(sep || ',')
      .map(function (x) {
        return x.trim();
      })
      .filter(Boolean);
  }

  // Caffeine level from a title plus tags: 'decaf' (Decaf and Wind Down), 'half' or ''.
  // Tags must match whole (a tag such as "YGroup_aafdecaf" is a grouping code, not a caffeine level).
  function caffeineOf(title, tags) {
    var t = norm(title);
    var tagList = [].concat(tags || []).map(norm);
    if (t.indexOf('half caff') !== -1 || t.indexOf('half-caff') !== -1 || tagList.indexOf('half caff') !== -1 || tagList.indexOf('half-caff') !== -1) return 'half';
    if (t.indexOf('decaf') !== -1 || t.indexOf('wind down') !== -1 || tagList.indexOf('decaf') !== -1) return 'decaf';
    return '';
  }

  // Lower is shown first. Profiles: 'decaf' (decaf, then half caff, then the rest), 'half' (half
  // caff, then decaf, then the rest), 'caf' (fully caffeinated first, half caff after; decaf is
  // filtered out before ranking). No profile: all equal.
  function caffeineRank(profile, level) {
    if (!profile) return 0;
    if (profile === 'caf') return level === 'half' ? 1 : level === 'decaf' ? 3 : 0;
    if (level === profile) return 0;
    if (level === 'decaf' || level === 'half') return 1;
    return 2;
  }

  class AlacRecommendations extends HTMLElement {
    connectedCallback() {
      var self = this;
      this.loaded = false;
      this.querySelectorAll('[data-alac-fixed] .alac-recs__add').forEach(function (button) {
        button.addEventListener('click', function () {
          self.add(button, parseInt(button.dataset.variantId, 10));
        });
      });
      this.profile = this.shopperCaffeine();
      if (this.dataset.mode === 'drawer') {
        var drawer = this.closest('cart-drawer');
        if (!drawer || drawer.classList.contains('active')) {
          this.load();
        } else {
          this.observer = new MutationObserver(function () {
            if (drawer.classList.contains('active')) {
              self.observer.disconnect();
              self.load();
            }
          });
          this.observer.observe(drawer, { attributes: true, attributeFilter: ['class'] });
        }
      } else {
        var start = function () { if (self.isConnected) self.load(); };
        var idle = function () {
          if ('requestIdleCallback' in window) window.requestIdleCallback(start, { timeout: 2000 });
          else setTimeout(start, 0);
        };
        if (document.readyState !== 'loading') idle();
        else document.addEventListener('DOMContentLoaded', idle, { once: true });
      }
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }

    // Profile computed server side from the cart (or PDP product): decaf, caf, half or ''.
    shopperCaffeine() {
      var level = this.dataset.caffeine || '';
      return level === 'decaf' || level === 'caf' || level === 'half' ? level : '';
    }

    load() {
      if (this.loaded) return;
      this.loaded = true;
      var self = this;
      var fixed = Array.prototype.map.call(this.querySelectorAll('[data-alac-fixed]'), function (li) {
        return li.dataset.productId;
      });
      var sources = splitList(this.dataset.productIds).slice(0, 3);
      var excludeIds = new Set(splitList(this.dataset.excludeIds).concat(sources, fixed));
      var roastNames = splitList(this.dataset.roasts).map(norm);
      var roastOf = function (title) {
        var t = norm(title);
        return roastNames.find(function (r) {
          return t.indexOf(r) !== -1;
        });
      };
      // Never suggest the same roast in another grind or format as something already chosen.
      var blockedRoasts = new Set(splitList(this.dataset.sourceTitles, '|').map(roastOf).filter(Boolean));
      var formats = splitList(this.dataset.formatCollections);
      var max = Math.max(0, (parseInt(this.dataset.max, 10) || 3) - fixed.length);

      Promise.all([
        Promise.all(sources.map(function (id) {
          return getIntent(id, 'complementary');
        })),
        Promise.all(formats.map(getCollection)),
        Promise.all(sources.map(function (id) {
          return getIntent(id, 'related');
        })),
      ]).then(function (res) {
        var profile = self.profile;
        var hideBulk = self.dataset.hideBulk === 'true';
        var candidates = [];
        var seen = new Set();
        var rankOf = function (p) {
          return caffeineRank(profile, caffeineOf(p.title, p.tags));
        };
        // Returns the entry when p may be shown, without picking it.
        var check = function (p, allowSameRoast) {
          if (!p) return null;
          var id = String(p.id);
          if (seen.has(id) || excludeIds.has(id) || p.gift_card || !p.available) return null;
          // One 12oz bag in the cart: no 5lb bag (the 3-bag offer is the next step up).
          if (hideBulk && /\(\s*5\s*lbs?\s*\)/i.test(p.title)) return null;
          // Caffeinated shoppers never get Decaf or Wind Down; Half Caff stays allowed (ranked after).
          if (profile === 'caf' && caffeineOf(p.title, p.tags) === 'decaf') return null;
          var roast = roastOf(p.title);
          // Curated complementary picks (same-roast K-cups, steep bags) are allowed; the block targets other grinds from related picks.
          if (!allowSameRoast && roast && blockedRoasts.has(roast)) return null;
          var variant = (p.variants || []).find(function (v) {
            return v.available;
          });
          if (!variant) return null;
          return { product: p, variant: variant, rank: rankOf(p) };
        };
        var add = function (entry) {
          if (!entry) return;
          seen.add(String(entry.product.id));
          candidates.push(entry);
        };
        var flat = function (lists) {
          return [].concat.apply([], lists);
        };
        // 1) Shopify complementary picks (set in Search & Discovery)
        flat(res[0]).forEach(function (p) {
          add(check(p, true));
        });
        // 2) Complementary formats: at most one per format collection (single serve cups, steep bags),
        //    the best caffeine match first. A same-roast cup or steep bag is fine when it matches a
        //    decaf or half caff shopper (Decaf cups for a Decaf bag).
        res[1].forEach(function (list) {
          var best = null;
          for (var i = 0; i < list.length; i++) {
            var p = list[i];
            var matches = profile && p && caffeineOf(p.title, p.tags) === profile;
            var entry = check(p, matches);
            if (entry && (!best || entry.rank < best.rank)) best = entry;
            if (best && best.rank === 0) break;
          }
          add(best);
        });
        // 3) Other roasts from Shopify related picks
        flat(res[2]).forEach(function (p) {
          add(check(p, false));
        });
        // Stable sort by caffeine rank (no profile: original order), then cap.
        var picked = candidates
          .map(function (entry, index) {
            entry.index = index;
            return entry;
          })
          .sort(function (a, b) {
            return a.rank - b.rank || a.index - b.index;
          })
          .slice(0, max);
        if (self.isConnected) self.render(picked);
      }).catch(function () {
        if (self.isConnected) self.render([]);
      });
    }

    formatMoney(cents) {
      var currency = this.dataset.currency || (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency: currency }).format(cents / 100);
      } catch (e) {
        return '$' + (cents / 100).toFixed(2);
      }
    }

    render(picked) {
      this.removeAttribute('aria-busy');
      var list = this.querySelector('.alac-recs__list');
      list.querySelectorAll('li:not([data-alac-fixed])').forEach(function (li) {
        li.remove();
      });
      if (!list.children.length && !picked.length) {
        this.hidden = true;
        return;
      }
      var self = this;
      picked.forEach(function (entry) {
        var p = entry.product;
        var v = entry.variant;
        var li = el('li', 'alac-recs__item');

        var link = el('a', 'alac-recs__link');
        link.href = p.url;
        link.title = p.title;
        var media = el('span', 'alac-recs__media');
        var imgSrc = (v.featured_image && v.featured_image.src) || p.featured_image;
        if (imgSrc) {
          var img = el('img');
          img.src = sizedImage(imgSrc, 200);
          img.alt = '';
          img.width = 100;
          img.height = 100;
          img.loading = 'lazy';
          img.decoding = 'async';
          media.appendChild(img);
        }
        link.appendChild(media);

        var info = el('span', 'alac-recs__info');
        info.appendChild(el('span', 'alac-recs__title', shortTitle(p.title)));
        var priceRow = el('span', 'alac-recs__price');
        var size = sizeLabel(p, v);
        if (size) {
          priceRow.appendChild(el('span', 'alac-recs__variant', size));
          priceRow.appendChild(el('span', 'alac-recs__sep', ' · '));
        }
        priceRow.appendChild(document.createTextNode(self.formatMoney(v.price)));
        info.appendChild(priceRow);
        link.appendChild(info);
        li.appendChild(link);

        var button = el('button', 'alac-recs__add', self.dataset.addLabel || 'Add');
        button.type = 'button';
        button.dataset.variantId = String(v.id);
        button.setAttribute('aria-label', (self.dataset.addLabel || 'Add') + ': ' + p.title + (v.title && v.title !== 'Default Title' ? ', ' + v.title : ''));
        button.addEventListener('click', function () {
          self.add(button, v.id);
        });
        li.appendChild(button);
        li.dataset.rank = String(entry.rank || 0);
        list.appendChild(li);
      });
      // Caffeine order across the fixed Best value item and the picks (fixed first on ties).
      var profile = this.profile;
      var items = Array.prototype.map.call(list.children, function (li, index) {
        var rank = li.hasAttribute('data-alac-fixed') ? caffeineRank(profile, caffeineOf(li.dataset.title, splitList(li.dataset.tags, '|'))) : parseInt(li.dataset.rank, 10) || 0;
        return { li: li, rank: rank, index: index };
      });
      items
        .sort(function (a, b) {
          return a.rank - b.rank || a.index - b.index;
        })
        .forEach(function (entry) {
          list.appendChild(entry.li);
        });
      this.hidden = false;
    }

    add(button, variantId) {
      var self = this;
      if (button.disabled) return;
      setBusy(button, true);
      mutate({
        source: 'alac-recommendations',
        task: function () {
          return postJSON('cart/add.js', withSections({ items: [{ id: variantId, quantity: 1 }] }));
        },
        success: self.dataset.addedMessage,
        focus: ['.alac-recs__add[data-variant-id="' + variantId + '"]', '.alac-recs__add'],
        errorSelector: self.dataset.mode === 'drawer' ? null : '[data-alac-recs-error]',
        onError: function () {
          if (!button.isConnected) return;
          setBusy(button, false);
          button.textContent = self.dataset.errorLabel || 'Try again';
        },
      })
        .then(function () {
          button.textContent = self.dataset.addedLabel || 'Added';
          if (self.isConnected && self.dataset.mode !== 'drawer') {
            setTimeout(function () {
              setBusy(button, false);
              button.textContent = self.dataset.addLabel || 'Add';
            }, 1500);
          }
        })
        .catch(function () {});
    }
  }
  window.customElements.define('alac-recommendations', AlacRecommendations);

  /* ---------- Body flag while the cart drawer is open (hides floating launchers via CSS) ---------- */
  function watchDrawer() {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    var sync = function () {
      document.body.classList.toggle('alac-cart-open', drawer.classList.contains('active'));
      if (drawer.classList.contains('active')) padDrawer();
    };
    new MutationObserver(sync).observe(drawer, { attributes: true, attributeFilter: ['class'] });
    sync();
    // Keyboard focus must not land under the pinned drawer header or checkout footer (WCAG 2.4.11).
    function padDrawer() {
      var inner = drawer.querySelector('.drawer__inner');
      if (!inner) return null;
      var head = inner.querySelector('.drawer__header');
      var foot = inner.querySelector('.drawer__footer');
      inner.style.setProperty('--alac-drawer-head', (head ? head.offsetHeight + 8 : 0) + 'px');
      inner.style.setProperty('--alac-drawer-foot', (foot ? foot.offsetHeight + 8 : 0) + 'px');
      return { inner: inner, head: head, foot: foot };
    }
    drawer.addEventListener('focusin', function (event) {
      var parts = padDrawer();
      if (!parts) return;
      var target = event.target;
      if ((parts.head && parts.head.contains(target)) || (parts.foot && parts.foot.contains(target))) return;
      if (target === parts.inner || !parts.inner.contains(target)) return;
      var rect = target.getBoundingClientRect();
      var top = parts.head ? parts.head.getBoundingClientRect().bottom : parts.inner.getBoundingClientRect().top;
      var bottom = parts.foot ? parts.foot.getBoundingClientRect().top : parts.inner.getBoundingClientRect().bottom;
      if (rect.height > bottom - top) return; // larger than the visible band: leave it
      if (rect.bottom > bottom - 4) parts.inner.scrollTop += rect.bottom - bottom + 12;
      else if (rect.top < top + 4) parts.inner.scrollTop -= top - rect.top + 12;
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', watchDrawer);
  else watchDrawer();
})();
